import { useState, type ReactNode } from 'react';

import type { Folder } from './sidebar.js';

/**
 * The folder dialogs (M2.6.1).
 *
 * These replace `window.prompt` and `window.confirm`, which is not a matter of
 * polish. A prompt can ask for one string and a confirm can ask one yes/no
 * question, and **deleting a folder needs neither**: it needs to ask where the
 * feeds inside it should go, which is a choice among that account's folders.
 * The API has taken `reassignTo` since M2.1.2 and nothing could reach it.
 */

export interface FolderDialogProps {
  /** Absent when creating. */
  readonly folder?: Folder | undefined;
  readonly folders: readonly Folder[];
  readonly busy: boolean;
  readonly error: string | undefined;
  readonly onSubmit: (name: string, parentId: string | null) => void;
  readonly onClose: () => void;
}

/** Create or rename, which differ only in what they start with. */
export function FolderDialog(props: FolderDialogProps): ReactNode {
  const existing = props.folder;
  const [name, setName] = useState(existing?.name ?? '');
  const [parentId, setParentId] = useState(existing?.parentId ?? '');

  /*
   * A folder cannot be moved into itself or into its own descendants. The API
   * refuses both with 422 and says why; leaving them out of the list means
   * nobody has to read an error to find out.
   */
  const candidates = props.folders.filter(
    (candidate) =>
      existing === undefined ||
      (candidate.folderId !== existing.folderId &&
        !isDescendant(candidate.folderId, existing.folderId, props.folders)),
  );

  return (
    <div
      className="overlay"
      role="dialog"
      aria-modal="true"
      aria-label={existing === undefined ? 'New folder' : 'Rename folder'}
    >
      <div className="overlay__card">
        <h2 className="overlay__title">
          {existing === undefined ? 'New folder' : `Rename ${existing.name}`}
        </h2>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim() !== '') {
              props.onSubmit(name.trim(), parentId === '' ? null : parentId);
            }
          }}
        >
          <label className="field">
            <span className="field__label">Name</span>
            <input
              className="field__input"
              type="text"
              value={name}
              autoFocus
              disabled={props.busy}
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
          </label>

          {candidates.length > 0 && (
            <label className="field">
              <span className="field__label">Inside</span>
              <select
                className="field__input"
                value={parentId}
                disabled={props.busy}
                onChange={(event) => {
                  setParentId(event.target.value);
                }}
              >
                <option value="">Nothing — keep it at the top</option>
                {candidates.map((candidate) => (
                  <option key={candidate.folderId} value={candidate.folderId}>
                    {candidate.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {props.error !== undefined && (
            <p className="auth__error" role="alert">
              {props.error}
            </p>
          )}

          <div className="overlay__actions">
            <button
              className="button button--primary"
              type="submit"
              disabled={props.busy || name.trim() === ''}
            >
              {props.busy ? 'Saving…' : existing === undefined ? 'Create' : 'Rename'}
            </button>
            <button
              className="button"
              type="button"
              disabled={props.busy}
              onClick={props.onClose}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export interface DeleteFolderDialogProps {
  readonly folder: Folder;
  readonly folders: readonly Folder[];
  /** How many subscriptions sit directly in it. */
  readonly feedCount: number;
  /** How many folders sit directly in it. */
  readonly childCount: number;
  readonly busy: boolean;
  readonly error: string | undefined;
  /** `reassignTo` — `null` leaves them unfiled. */
  readonly onConfirm: (reassignTo: string | null) => void;
  readonly onClose: () => void;
}

/**
 * Deleting a folder, and the question that makes it safe (M2.6.1, M2.1.2).
 *
 * The wording does the work here. "Delete folder?" invites the reader to
 * imagine the feeds going too — so this says what happens to them, and then
 * lets them choose. Child folders are stated rather than asked about, because
 * the API always reparents them and offering a choice would imply the
 * alternative exists.
 */
export function DeleteFolderDialog(props: DeleteFolderDialogProps): ReactNode {
  const [reassignTo, setReassignTo] = useState('');

  const elsewhere = props.folders.filter(
    (candidate) =>
      candidate.folderId !== props.folder.folderId &&
      !isDescendant(candidate.folderId, props.folder.folderId, props.folders),
  );

  return (
    <div
      className="overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Delete ${props.folder.name}`}
    >
      <div className="overlay__card">
        <h2 className="overlay__title">Delete {props.folder.name}?</h2>

        <p className="overlay__text">
          {props.feedCount === 0
            ? 'It has no feeds in it.'
            : `The ${props.feedCount === 1 ? 'feed' : String(props.feedCount) + ' feeds'} inside stay subscribed — nothing is unsubscribed and no read state is lost.`}
          {props.childCount > 0 &&
            ` The ${props.childCount === 1 ? 'folder' : String(props.childCount) + ' folders'} inside move up a level rather than being deleted.`}
        </p>

        {props.feedCount > 0 && elsewhere.length > 0 && (
          <label className="field">
            <span className="field__label">Move those feeds to</span>
            <select
              className="field__input"
              value={reassignTo}
              disabled={props.busy}
              onChange={(event) => {
                setReassignTo(event.target.value);
              }}
            >
              <option value="">No folder</option>
              {elsewhere.map((candidate) => (
                <option key={candidate.folderId} value={candidate.folderId}>
                  {candidate.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {props.error !== undefined && (
          <p className="auth__error" role="alert">
            {props.error}
          </p>
        )}

        <div className="overlay__actions">
          <button
            className="button button--primary"
            type="button"
            disabled={props.busy}
            onClick={() => {
              props.onConfirm(reassignTo === '' ? null : reassignTo);
            }}
          >
            {props.busy ? 'Deleting…' : 'Delete the folder'}
          </button>
          <button
            className="button"
            type="button"
            disabled={props.busy}
            onClick={props.onClose}
          >
            Keep it
          </button>
        </div>
      </div>
    </div>
  );
}

export interface MoveFeedDialogProps {
  readonly title: string;
  readonly folders: readonly Folder[];
  readonly currentFolderId: string | undefined;
  readonly busy: boolean;
  readonly error: string | undefined;
  readonly onConfirm: (folderId: string | null) => void;
  readonly onClose: () => void;
}

/**
 * Moving one feed between folders, without a mouse.
 *
 * M2.6.1 asks for drag and drop, and the sidebar has it — but a drag is not
 * reachable from a keyboard, and "reorganise your feeds" is exactly the task
 * somebody would want to do without one. This is the same operation as a
 * drop, offered as a control.
 */
export function MoveFeedDialog(props: MoveFeedDialogProps): ReactNode {
  const [folderId, setFolderId] = useState(props.currentFolderId ?? '');

  return (
    <div
      className="overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Move ${props.title}`}
    >
      <div className="overlay__card">
        <h2 className="overlay__title">Move {props.title}</h2>

        <label className="field">
          <span className="field__label">Folder</span>
          <select
            className="field__input"
            value={folderId}
            autoFocus
            disabled={props.busy}
            onChange={(event) => {
              setFolderId(event.target.value);
            }}
          >
            <option value="">No folder</option>
            {props.folders.map((folder) => (
              <option key={folder.folderId} value={folder.folderId}>
                {folder.name}
              </option>
            ))}
          </select>
        </label>

        {props.error !== undefined && (
          <p className="auth__error" role="alert">
            {props.error}
          </p>
        )}

        <div className="overlay__actions">
          <button
            className="button button--primary"
            type="button"
            disabled={props.busy}
            onClick={() => {
              props.onConfirm(folderId === '' ? null : folderId);
            }}
          >
            {props.busy ? 'Moving…' : 'Move'}
          </button>
          <button
            className="button"
            type="button"
            disabled={props.busy}
            onClick={props.onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/** Whether `candidate` sits somewhere beneath `ancestor`. */
export function isDescendant(
  candidate: string,
  ancestor: string,
  folders: readonly Folder[],
): boolean {
  let current = folders.find((folder) => folder.folderId === candidate);
  let steps = 0;

  // Bounded, so data that already contains a cycle cannot hang the sidebar.
  while (current !== undefined && steps <= folders.length) {
    if (current.parentId === ancestor) {
      return true;
    }
    if (current.parentId === undefined || current.parentId === null) {
      return false;
    }
    const parentId: string = current.parentId;
    current = folders.find((folder) => folder.folderId === parentId);
    steps += 1;
  }

  return false;
}
